import { useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';

import { PERFORMANCE_ERROR } from '../../hooks/useMemberPerformanceProfile';
import { Card, MeterBar, Section, StatusChip } from './Card';
import {
  COLORS,
  DEFAULT_TARGETS,
  LOWER_BODY_RANGE,
  PUSH_PULL_GAP,
  PUSH_PULL_MAX_RANGE,
  PUSH_PULL_MIN_RANGE,
  SETS_MAX,
  SETS_MIN,
  TARGET_LIFTS,
  TARGET_STATUS_LABELS,
  TARGET_STATUS_TONES,
} from './constants';
import { formatNumber } from './format';

const PUSH_PULL_STEP = 0.05;
const LOWER_BODY_STEP = 1;
const GENERIC_ERROR = 'Could not save your targets. Please try again.';
const EPSILON = 1e-9;

const toNumber = (value) => (String(value).trim() === '' ? NaN : Number(value));

// Returns field errors; an empty object means valid. Limits mirror the backend contract.
export const validateTargets = ({ pushPullMin, pushPullMax, lowerBodyMinPct }) => {
  const errors = {};
  const min = toNumber(pushPullMin);
  const max = toNumber(pushPullMax);
  const lower = toNumber(lowerBodyMinPct);
  if (!Number.isFinite(min)) {
    errors.pushPullMin = 'Enter a number.';
  } else if (min < PUSH_PULL_MIN_RANGE.min - EPSILON || min > PUSH_PULL_MIN_RANGE.max + EPSILON) {
    errors.pushPullMin = `The minimum must be between ${PUSH_PULL_MIN_RANGE.min} and ${PUSH_PULL_MIN_RANGE.max}.`;
  }
  if (!Number.isFinite(max)) {
    errors.pushPullMax = 'Enter a number.';
  } else if (max < PUSH_PULL_MAX_RANGE.min - EPSILON || max > PUSH_PULL_MAX_RANGE.max + EPSILON) {
    errors.pushPullMax = `The maximum must be between ${PUSH_PULL_MAX_RANGE.min} and ${PUSH_PULL_MAX_RANGE.max}.`;
  }
  if (!errors.pushPullMin && !errors.pushPullMax && max < min + PUSH_PULL_GAP - EPSILON) {
    errors.pushPullMax = `The maximum must be at least ${PUSH_PULL_GAP} higher than the minimum.`;
  }
  if (!Number.isFinite(lower) || !Number.isInteger(lower)) {
    errors.lowerBodyMinPct = 'Enter a whole number.';
  } else if (lower < LOWER_BODY_RANGE.min || lower > LOWER_BODY_RANGE.max) {
    errors.lowerBodyMinPct = `The lower-body minimum must be between ${LOWER_BODY_RANGE.min} and ${LOWER_BODY_RANGE.max}.`;
  }
  return errors;
};

const formFromTargets = (targets) => {
  const t = targets || DEFAULT_TARGETS;
  const sets = {};
  TARGET_LIFTS.forEach(({ key }) => {
    sets[key] = t.setsPerLift?.[key] ?? DEFAULT_TARGETS.setsPerLift[key];
  });
  return {
    setsPerLift: sets,
    pushPullMin: String(t.pushPullMin ?? DEFAULT_TARGETS.pushPullMin),
    pushPullMax: String(t.pushPullMax ?? DEFAULT_TARGETS.pushPullMax),
    lowerBodyMinPct: String(t.lowerBodyMinPct ?? DEFAULT_TARGETS.lowerBodyMinPct),
  };
};

const range = ({ min, max }, digits) => `${min.toFixed(digits)} and ${max.toFixed(digits)}`;

const labelFor = (key) => TARGET_LIFTS.find((lift) => lift.key === key)?.label || key;

const StatusRow = ({ title, detail, status = null, bar }) => (
  <Box sx={{ py: 1.5, borderTop: `1px solid ${COLORS.border}` }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
      <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{title}</Typography>
      {status !== null && (
        <StatusChip
          label={TARGET_STATUS_LABELS[status] || TARGET_STATUS_LABELS.insufficient}
          tone={TARGET_STATUS_TONES[status] || 'neutral'}
        />
      )}
    </Box>
    <Typography sx={{ fontSize: 14, opacity: 0.85, mb: bar ? 1 : 0 }}>{detail}</Typography>
    {bar}
  </Box>
);

// Text fragments are omitted, never printed as "undefined", when the backend leaves values out.
const statusLabel = (status) => TARGET_STATUS_LABELS[status] || null;
const withStatus = (text, status) =>
  statusLabel(status) ? `${text}, ${statusLabel(status)}` : text;
const rangeText = (min, max) =>
  Number.isFinite(min) && Number.isFinite(max) ? ` Target ${min} to ${max}.` : '';
const minimumText = (min) => (Number.isFinite(min) ? ` Target at least ${min}%.` : '');
const perSessionText = (lift) =>
  Number.isFinite(lift.targetPerSession) ? ` Target ${lift.targetPerSession} per session.` : '';

const hasAverage = (lift) => Number.isFinite(lift.avgSetsPerSession);

const liftStatus = (lift) => (hasAverage(lift) ? lift.status : null);

const liftDetail = (lift) => {
  const target = perSessionText(lift);
  if (hasAverage(lift)) {
    return `${formatNumber(lift.avgSetsPerSession, 1)} sets per session on average.${target}`;
  }
  if (lift.avgSetsPerSession === null) {
    return `Not enough sessions yet.${target}`;
  }
  const weekly = Number.isFinite(lift.weeklySets)
    ? `${formatNumber(lift.weeklySets, 1)} sets a week`
    : 'No weekly sets yet';
  return `${weekly}.${target}`;
};

const Progress = ({ progress }) => {
  const pp = progress?.pushPull;
  const lower = progress?.lowerBodyPct;
  const lifts = Array.isArray(progress?.lifts) ? progress.lifts : [];
  return (
    <Box>
      {pp && (
        <StatusRow
          title="Push/pull balance"
          status={pp.status}
          detail={
            Number.isFinite(pp.value)
              ? `Push to pull ratio ${formatNumber(pp.value, 2)}.${rangeText(pp.min, pp.max)}`
              : `No ratio yet.${rangeText(pp.min, pp.max)}`
          }
          bar={
            <MeterBar
              label="Push to pull ratio"
              value={pp.value}
              max={2}
              valueText={
                Number.isFinite(pp.value)
                  ? withStatus(formatNumber(pp.value, 2), pp.status)
                  : 'No data yet'
              }
              color={COLORS[TARGET_STATUS_TONES[pp.status]] || COLORS.neutral}
            />
          }
        />
      )}
      {lower && (
        <StatusRow
          title="Lower-body share"
          status={lower.status}
          detail={
            Number.isFinite(lower.value)
              ? `${formatNumber(lower.value, 0)}% of your sets.${minimumText(lower.min)}`
              : `No share yet.${minimumText(lower.min)}`
          }
          bar={
            <MeterBar
              label="Lower-body share of sets"
              value={lower.value}
              max={100}
              valueText={
                Number.isFinite(lower.value)
                  ? withStatus(`${formatNumber(lower.value, 0)} percent`, lower.status)
                  : 'No data yet'
              }
              color={COLORS[TARGET_STATUS_TONES[lower.status]] || COLORS.neutral}
            />
          }
        />
      )}
      {lifts.map((lift) => (
        <StatusRow
          key={lift.exerciseName}
          title={lift.label || labelFor(lift.exerciseName)}
          status={liftStatus(lift)}
          detail={liftDetail(lift)}
        />
      ))}
    </Box>
  );
};

const Stepper = ({ label, value, onChange, disabled }) => (
  <Box
    role="group"
    aria-label={`${label} sets per session`}
    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, py: 0.5 }}
  >
    <Typography component="span" sx={{ fontSize: 15 }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <IconButton
        size="small"
        aria-label={`Fewer sets for ${label}`}
        aria-disabled={disabled || value <= SETS_MIN ? 'true' : undefined}
        onClick={() => !disabled && value > SETS_MIN && onChange(value - 1)}
        sx={{ color: 'inherit', border: `1px solid ${COLORS.border}` }}
      >
        <span aria-hidden="true">-</span>
      </IconButton>
      <Box
        component="output"
        aria-live="polite"
        aria-label={`${label} sets per session`}
        data-testid={`sets-${label}`}
        sx={{ minWidth: 28, textAlign: 'center', fontWeight: 700 }}
      >
        {value}
      </Box>
      <IconButton
        size="small"
        aria-label={`More sets for ${label}`}
        aria-disabled={disabled || value >= SETS_MAX ? 'true' : undefined}
        onClick={() => !disabled && value < SETS_MAX && onChange(value + 1)}
        sx={{ color: 'inherit', border: `1px solid ${COLORS.border}` }}
      >
        <span aria-hidden="true">+</span>
      </IconButton>
    </Box>
  </Box>
);

const Targets = ({ targets, targetProgress, onSave, onReset, onSignIn, feedback, setFeedback }) => {
  const [form, setForm] = useState(() => formFromTargets(targets));
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(null);

  // Follow the saved targets (compared as normalised form values, so key order does not
  // matter), but never overwrite unsaved edits: a refetch while the form is dirty keeps them.
  const serverKey = JSON.stringify(formFromTargets(targets));
  const baselineKey = useRef(serverKey);
  const formRef = useRef(form);
  formRef.current = form;
  useEffect(() => {
    if (serverKey === baselineKey.current) {
      return;
    }
    const wasDirty = JSON.stringify(formRef.current) !== baselineKey.current;
    baselineKey.current = serverKey;
    if (!wasDirty) {
      setForm(JSON.parse(serverKey));
      setErrors({});
    }
  }, [serverKey]);

  const setField = (name) => (event) => {
    setForm((prev) => ({ ...prev, [name]: event.target.value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };
  const setSets = (key) => (value) =>
    setForm((prev) => ({ ...prev, setsPerLift: { ...prev.setsPerLift, [key]: value } }));

  const fail = (err) => {
    if (err?.kind === PERFORMANCE_ERROR.SIGN_IN) {
      onSignIn?.();
      return;
    }
    setFeedback({ source: 'targets', type: 'error', text: err?.userMessage || GENERIC_ERROR });
  };

  const submit = async (event) => {
    event.preventDefault();
    if (busy) {
      return;
    }
    setFeedback(null);
    const found = validateTargets(form);
    setErrors(found);
    const first = ['pushPullMin', 'pushPullMax', 'lowerBodyMinPct'].find((f) => found[f]);
    if (first) {
      document.getElementById(`target-${first}`)?.focus();
      return;
    }
    setBusy('save');
    try {
      await onSave({
        setsPerLift: form.setsPerLift,
        pushPullMin: Number(form.pushPullMin),
        pushPullMax: Number(form.pushPullMax),
        lowerBodyMinPct: Number(form.lowerBodyMinPct),
      });
      setFeedback({ source: 'targets', type: 'status', text: 'Targets saved.' });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const reset = async () => {
    if (busy) {
      return;
    }
    setFeedback(null);
    setBusy('reset');
    try {
      await onReset();
      setForm(formFromTargets(DEFAULT_TARGETS));
      setErrors({});
      setFeedback({ source: 'targets', type: 'status', text: 'Targets reset to defaults.' });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const sets = form.setsPerLift;
  const locked = Boolean(busy);
  const lockProps = locked ? { readOnly: true, 'aria-disabled': 'true' } : {};

  return (
    <Section title="Your targets" id="perf-targets">
      <Card>
        <Typography sx={{ fontSize: 14, opacity: 0.85 }}>
          {targets?.isCustom
            ? 'These are your own targets.'
            : 'These are the default targets until you set your own.'}{' '}
          Your next session plan and the bars below follow them.
        </Typography>
        {targetProgress && <Progress progress={targetProgress} />}

        <Box
          component="form"
          noValidate
          onSubmit={submit}
          aria-label="Edit your targets"
          sx={{ mt: 3 }}
        >
          <Typography variant="h3" sx={{ fontSize: 16, fontWeight: 700 }}>
            Sets per lift, per session
          </Typography>
          <Typography sx={{ fontSize: 13, opacity: 0.8, mb: 1 }}>
            Choose {SETS_MIN} to {SETS_MAX} sets for each lift.
          </Typography>
          {TARGET_LIFTS.map(({ key, label }) => (
            <Stepper
              key={key}
              label={label}
              value={sets[key]}
              onChange={setSets(key)}
              disabled={locked}
            />
          ))}
          <Box
            sx={{
              mt: 2,
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', tl: 'repeat(3, 1fr)' },
            }}
          >
            <TextField
              id="target-pushPullMin"
              label="Push/pull minimum"
              size="small"
              type="number"
              value={form.pushPullMin}
              onChange={setField('pushPullMin')}
              inputProps={{
                ...lockProps,
                step: PUSH_PULL_STEP,
                min: PUSH_PULL_MIN_RANGE.min,
                max: PUSH_PULL_MIN_RANGE.max,
              }}
              error={Boolean(errors.pushPullMin)}
              helperText={errors.pushPullMin || `Between ${range(PUSH_PULL_MIN_RANGE, 1)}`}
            />
            <TextField
              id="target-pushPullMax"
              label="Push/pull maximum"
              size="small"
              type="number"
              value={form.pushPullMax}
              onChange={setField('pushPullMax')}
              inputProps={{
                ...lockProps,
                step: PUSH_PULL_STEP,
                min: PUSH_PULL_MAX_RANGE.min,
                max: PUSH_PULL_MAX_RANGE.max,
              }}
              error={Boolean(errors.pushPullMax)}
              helperText={errors.pushPullMax || `Between ${range(PUSH_PULL_MAX_RANGE, 1)}`}
            />
            <TextField
              id="target-lowerBodyMinPct"
              label="Lower-body minimum (%)"
              size="small"
              type="number"
              value={form.lowerBodyMinPct}
              onChange={setField('lowerBodyMinPct')}
              inputProps={{
                ...lockProps,
                step: LOWER_BODY_STEP,
                min: LOWER_BODY_RANGE.min,
                max: LOWER_BODY_RANGE.max,
              }}
              error={Boolean(errors.lowerBodyMinPct)}
              helperText={errors.lowerBodyMinPct || `Between ${range(LOWER_BODY_RANGE, 0)}`}
            />
          </Box>
          {feedback?.source === 'targets' && feedback.type === 'error' && (
            <Typography role="alert" sx={{ mt: 2, fontSize: 14, color: COLORS.caution }}>
              {feedback.text}
            </Typography>
          )}
          {feedback?.source === 'targets' && feedback.type === 'status' && (
            <Typography data-testid="targets-status" sx={{ mt: 2, fontSize: 14 }}>
              {feedback.text}
            </Typography>
          )}
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button type="submit" variant="contained" aria-disabled={locked ? 'true' : undefined}>
              {busy === 'save' ? 'Saving...' : 'Save targets'}
            </Button>
            <Button
              type="button"
              variant="outlined"
              aria-disabled={locked ? 'true' : undefined}
              onClick={reset}
            >
              {busy === 'reset' ? 'Resetting...' : 'Reset to defaults'}
            </Button>
          </Box>
        </Box>
      </Card>
    </Section>
  );
};

export default Targets;
